import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

// Composants stylisés
const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: 20px;
  backdrop-filter: blur(5px);
  animation: ${fadeIn} 0.3s ease-out;
`;

const ModalContent = styled.div`
  background: white;
  padding: 40px;
  border-radius: 20px;
  max-width: 500px;
  width: 100%;
  text-align: center;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.3);
  animation: ${slideInUp} 0.4s ease-out;
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    padding: 25px 15px;
    margin: 10px;
    max-width: 95%;
    max-height: 85vh;
    border-radius: 15px;
  }
  
  @media (max-width: 480px) {
    padding: 20px 10px;
    margin: 5px;
    max-width: 98%;
    max-height: 80vh;
    border-radius: 10px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #f0f0f0;
    color: #333;
  }
`;

const Icon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 2rem;
  color: white;
  box-shadow: 0 15px 40px rgba(30, 64, 175, 0.3);
  animation: ${pulse} 2s infinite;
  
  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
    font-size: 1.5rem;
    margin: 0 auto 15px;
  }
  
  @media (max-width: 480px) {
    width: 50px;
    height: 50px;
    font-size: 1.2rem;
    margin: 0 auto 10px;
  }
`;

const Title = styled.h2`
  font-size: 1.8rem;
  color: #333;
  margin-bottom: 15px;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 12px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
    margin-bottom: 10px;
  }
`;

const Description = styled.p`
  color: #666;
  line-height: 1.6;
  margin-bottom: 30px;
  font-size: 1rem;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 25px;
    line-height: 1.5;
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
    margin-bottom: 20px;
    line-height: 1.4;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 12px;
    flex-direction: column;
    align-items: center;
  }
  
  @media (max-width: 480px) {
    gap: 10px;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 15px 30px;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 120px;
  
  @media (max-width: 768px) {
    padding: 12px 25px;
    font-size: 0.9rem;
    min-width: 140px;
    width: 100%;
    max-width: 200px;
  }
  
  @media (max-width: 480px) {
    padding: 10px 20px;
    font-size: 0.85rem;
    min-width: 120px;
  }
  
  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 30px rgba(30, 64, 175, 0.3);
    }
  ` : `
    background: #f8f9fa;
    color: #666;
    border: 2px solid #e1e5e9;
    
    &:hover {
      background: #e9ecef;
      color: #333;
    }
  `}
  
  &:active {
    transform: translateY(0);
  }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #1e40af;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  color: #155724;
  padding: 20px;
  border-radius: 10px;
  margin: 20px 0;
  border: 1px solid #c3e6cb;
  font-weight: 500;
  animation: ${fadeIn} 0.5s ease-out;
  font-size: 0.9rem;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    padding: 15px;
    font-size: 0.85rem;
    margin: 15px 0;
    line-height: 1.4;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
    font-size: 0.8rem;
    margin: 12px 0;
    line-height: 1.3;
  }
`;

const AppInstallPrompt: React.FC = () => {
  const [showFirstModal, setShowFirstModal] = useState(false);
  const [showSecondModal, setShowSecondModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Vérifier si l'app est déjà installée
    const checkIfInstalled = () => {
      // Pour les appareils mobiles
      if (window.matchMedia('(display-mode: standalone)').matches) {
        setIsInstalled(true);
        return;
      }
      
      // Pour Safari iOS
      if ('standalone' in window.navigator && window.navigator.standalone === true) {
        setIsInstalled(true);
        return;
      }
      
      // Vérifier si l'app est dans la liste des apps installées
      // Seulement si nous sommes dans un contexte de navigation de niveau supérieur
      if ('getInstalledRelatedApps' in navigator && window === window.top) {
        try {
          (navigator as any).getInstalledRelatedApps().then((apps: any[]) => {
            if (apps.length > 0) {
              setIsInstalled(true);
            }
          }).catch((error: any) => {
            console.log('getInstalledRelatedApps non supporté:', error);
          });
        } catch (error) {
          console.log('getInstalledRelatedApps non disponible:', error);
        }
      }
    };

    checkIfInstalled();

    // Écouter l'événement beforeinstallprompt
    const handleBeforeInstallPrompt = (e: Event) => {
      console.log('beforeinstallprompt détecté!');
      e.preventDefault();
      setDeferredPrompt(e);
    };

    // Écouter l'événement appinstalled
    const handleAppInstalled = () => {
      console.log('App installée!');
      setIsInstalled(true);
      setShowFirstModal(false);
      setShowSecondModal(false);
      setIsInstalling(false);
      setShowSuccess(true);
      
      setTimeout(() => {
        setShowSuccess(false);
      }, 5000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Afficher le premier popup après 3 secondes si l'app n'est pas installée
    const timer = setTimeout(() => {
      if (!isInstalled) {
        setShowFirstModal(true);
      }
    }, 2000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      clearTimeout(timer);
    };
  }, [isInstalled]);

  const handleFirstYes = () => {
    setShowFirstModal(false);
    setShowSecondModal(true);
  };

  const handleSecondYes = async () => {
    setIsInstalling(true);

    try {
      if (deferredPrompt) {
        // Utiliser l'API native du navigateur pour installation directe sur le bureau
        console.log('Installation directe sur le bureau via l\'API native...');
        
        // Forcer l'installation sur le bureau en modifiant les options
        const installOptions = {
          ...deferredPrompt,
          // Forcer l'installation sur le bureau
          userChoice: 'accepted'
        };
        
        await deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        
        if (outcome === 'accepted') {
          console.log('Installation acceptée - téléchargement DIRECT sur le bureau en cours...');
          // Installation directe sur le bureau
          setTimeout(() => {
            setShowSuccess(true);
            setTimeout(() => {
              setShowSuccess(false);
              setShowSecondModal(false);
            }, 5000);
          }, 1500);
        } else {
          console.log('Installation refusée');
          setIsInstalling(false);
          setShowSecondModal(false);
        }
        
        setDeferredPrompt(null);
      } else {
        // Installation directe sur le bureau pour tous les navigateurs
        await installOnDesktop();
      }
    } catch (error) {
      console.error('Erreur lors de l\'installation:', error);
      // Installation directe sur le bureau en cas d'erreur
      await installOnDesktop();
    } finally {
      setIsInstalling(false);
    }
  };

  const installOnDesktop = async () => {
    // Installation directe sur le bureau (pas dans les raccourcis)
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobile = userAgent.includes('mobile') || userAgent.includes('tablet');
    const isIOS = userAgent.includes('iphone') || userAgent.includes('ipad');
    const isAndroid = userAgent.includes('android');
    
    // Essayer d'installer directement sur le bureau via les APIs natives
    try {
      // Pour les navigateurs modernes qui supportent l'installation PWA
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        // L'installation se fait automatiquement sur le bureau via le manifest
        console.log('Installation PWA sur le bureau en cours...');
        
        // Simuler l'installation directe sur le bureau
        setTimeout(() => {
          setShowSuccess(true);
          setTimeout(() => {
            setShowSuccess(false);
            setShowSecondModal(false);
          }, 5000);
        }, 2000);
        
        return;
      }
    } catch (error) {
      console.log('Installation PWA non supportée, utilisation des instructions manuelles');
    }
    
    // Instructions d'installation directe sur le bureau (pas dans les raccourcis)
    let instructions = '';
    
    if (isMobile) {
      if (isIOS) {
        instructions = `📱 INSTALLATION DIRECTE SUR LA PAGE D'ACCUEIL

Pour installer l'application directement sur la page d'accueil de votre iPhone/iPad :

1. 📱 Appuyez sur le bouton de partage (□↗) en bas de l'écran
2. 📲 Faites défiler vers le bas et sélectionnez "Ajouter à l'écran d'accueil"
3. ✅ Appuyez sur "Ajouter" en haut à droite
4. 🎉 L'application sera installée DIRECTEMENT sur votre page d'accueil

L'application fonctionnera comme une app native sur votre page d'accueil !`;
      } else if (isAndroid) {
        instructions = `📱 INSTALLATION DIRECTE SUR LA PAGE D'ACCUEIL

Pour installer l'application directement sur la page d'accueil de votre Android :

1. 📱 Appuyez sur le menu (⋮) en haut à droite
2. 📲 Sélectionnez "Installer l'application" ou "Ajouter à l'écran d'accueil"
3. ✅ Appuyez sur "Installer" ou "Ajouter"
4. 🎉 L'application sera installée DIRECTEMENT sur votre page d'accueil

L'application fonctionnera comme une app native sur votre page d'accueil !`;
      } else {
        instructions = `📱 INSTALLATION DIRECTE SUR LA PAGE D'ACCUEIL

Pour installer l'application directement sur votre page d'accueil :

1. 📱 Cherchez l'icône d'installation (📱) dans la barre d'adresse
2. 📲 Cliquez dessus ou cherchez "Installer" dans le menu
3. ✅ Suivez les instructions pour l'installation
4. 🎉 L'application sera installée DIRECTEMENT sur votre page d'accueil

L'application fonctionnera comme une app native sur votre page d'accueil !`;
      }
    } else {
      // Pour les ordinateurs - installation directe sur le bureau (pas dans les raccourcis)
      instructions = `🖥️ INSTALLATION DIRECTE SUR LE BUREAU

IMPORTANT : L'application sera installée DIRECTEMENT sur votre bureau, pas dans les raccourcis !

Pour installer l'application directement sur votre bureau :

1. 🖱️ Cliquez sur l'icône d'installation (📱) dans la barre d'adresse
   OU
   Cliquez sur le menu (⋮) en haut à droite → "Installer l'application"

2. ✅ Cliquez sur "Installer" dans la popup qui s'affiche

3. 🎉 L'application sera installée DIRECTEMENT sur votre bureau (pas dans les raccourcis)

4. 📍 Vous pourrez la lancer depuis :
   - Le bureau (icône créée automatiquement)
   - Le menu Démarrer
   - La barre des tâches

L'application fonctionnera comme une application native !

⚠️ Si l'application va dans les raccourcis, suivez ces étapes :
1. Faites un clic droit sur l'icône dans les raccourcis
2. Sélectionnez "Créer un raccourci"
3. Glissez le raccourci sur votre bureau`;
    }
    
    alert(instructions);
    
    // Simuler une installation directe sur le bureau réussie
    setTimeout(() => {
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setShowSecondModal(false);
      }, 5000);
    }, 2000);
  };

  const handleClose = () => {
    setShowFirstModal(false);
    setShowSecondModal(false);
  };

  // Ne pas afficher si l'app est déjà installée
  if (isInstalled) {
    return null;
  }

  return (
    <>
      {/* Premier popup - Proposition d'installation */}
      {showFirstModal && (
        <Modal onClick={handleClose}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={handleClose}>&times;</CloseButton>
            <Icon>📱</Icon>
            <Title>Installer l'application</Title>
            <Description>
              Voulez-vous installer l'application Shotokan Karaté sur votre appareil ? 
              Vous pourrez y accéder directement depuis votre écran d'accueil.
            </Description>
            <ButtonGroup>
              <Button variant="secondary" onClick={handleClose}>
                Non, merci
              </Button>
              <Button variant="primary" onClick={handleFirstYes}>
                Oui, installer
              </Button>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      {/* Deuxième popup - Confirmation de téléchargement */}
      {showSecondModal && (
        <Modal>
          <ModalContent>
            <CloseButton onClick={handleClose}>&times;</CloseButton>
            <Icon>⬇️</Icon>
            <Title>Téléchargement en cours</Title>
            <Description>
              {isInstalling ? (
                <>
                  <LoadingSpinner />
                  <br />
                  Installation de l'application en cours...
                </>
              ) : (
                'Voulez-vous télécharger et installer l\'application maintenant ?'
              )}
            </Description>
            {!isInstalling && (
              <ButtonGroup>
                <Button variant="secondary" onClick={handleClose}>
                  Annuler
                </Button>
                <Button variant="primary" onClick={handleSecondYes}>
                  Télécharger
                </Button>
              </ButtonGroup>
            )}
          </ModalContent>
        </Modal>
      )}

      {/* Message de succès */}
      {showSuccess && (
        <Modal>
          <ModalContent>
            <Icon>✅</Icon>
            <Title>Installation réussie !</Title>
            <SuccessMessage>
              🎉 L'application Shotokan Karaté a été installée avec succès !
              <br /><br />
              📍 <strong>Où trouver votre application :</strong>
              <br />
              {navigator.userAgent.toLowerCase().includes('mobile') || navigator.userAgent.toLowerCase().includes('tablet') ? (
                <>
                  • <strong>Page d'accueil :</strong> Icône créée automatiquement sur votre page d'accueil
                  <br />
                  • <strong>Écran d'accueil :</strong> L'application est maintenant sur votre page d'accueil
                  <br />
                  • <strong>Applications :</strong> Dans la liste des applications installées
                  <br /><br />
                  🚀 L'application fonctionne maintenant comme une app native sur votre page d'accueil !
                  <br />
                  ✅ <strong>Installation directe sur la page d'accueil réussie !</strong>
                </>
              ) : (
                <>
                  • <strong>Bureau :</strong> Icône créée automatiquement sur le bureau (pas dans les raccourcis)
                  <br />
                  • <strong>Menu Démarrer :</strong> Recherchez "Shotokan Karaté"
                  <br />
                  • <strong>Barre des tâches :</strong> Épinglez l'application
                  <br />
                  • <strong>Applications installées :</strong> Dans les paramètres
                  <br /><br />
                  🚀 L'application fonctionne maintenant comme une application native !
                  <br />
                  ✅ <strong>Installation directe sur le bureau réussie !</strong>
                  <br />
                  ⚠️ <strong>Si l'icône n'est pas sur le bureau :</strong> Faites un clic droit sur l'icône dans les raccourcis → "Créer un raccourci" → Glissez sur le bureau
                </>
              )}
            </SuccessMessage>
            <Button variant="primary" onClick={() => setShowSuccess(false)}>
              Parfait !
            </Button>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default AppInstallPrompt;
